/**
 * Migration Pipeline Orchestrator — Phase 3b plumbing.
 *
 * Main-thread wrapper around the Web Worker in `./worker.ts`. The
 * orchestrator owns worker lifecycle, message correlation, and progress
 * fan-out. It does not yet wire into the wizard UI; Phase 3c hooks it
 * up to the "Generate" step once the HTML report renderer lands.
 */

import type {
  ErrorMessage,
  ParsedMessage,
  ProgressMessage,
  WorkerOutboundMessage,
} from './worker.js';
import type { ProbeTargetResult } from './probe/orchestrator.js';

/**
 * Progress stages emitted by the full browser pipeline. `parse` and
 * `validate` originate in the Web Worker (see `ProgressMessage`); the
 * remaining stages are emitted on the main thread by `pipeline.ts`
 * after the worker resolves.
 */
export type PipelineProgressStage = ProgressMessage['stage'] | 'map' | 'generate' | 'render-model';

export interface OrchestratorProgressEvent {
  stage: PipelineProgressStage;
  message: string;
}

export interface OrchestratorOptions {
  /**
   * URL to the bundled worker script. Defaults to the relative
   * `assets/migration-worker.js` path produced by `npm run build:client`.
   * Tests inject a `Worker` factory instead of a URL via `workerFactory`.
   */
  workerUrl?: string;
  /**
   * Optional factory for unit-testing the orchestrator without a real
   * `Worker`. The factory must return an object that conforms to the
   * subset of the DOM `Worker` API used by the orchestrator.
   */
  workerFactory?: () => MinimalWorker;
  /**
   * Phase 5 — forwarded to the worker as the master probe enable flag.
   * Defaults to true. When false, every probe target is reported as
   * `probe-disabled`.
   */
  probesEnabled?: boolean;
}

/**
 * Subset of the DOM `Worker` interface used by the orchestrator. Tests
 * implement this with a plain class.
 */
export interface MinimalWorker {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (ev: MessageEvent<WorkerOutboundMessage>) => void): void;
  addEventListener(type: 'error', listener: (ev: ErrorEvent) => void): void;
  terminate(): void;
}

export interface ParsePipelineResult {
  config: ParsedMessage['config'];
  validation: ParsedMessage['validation'];
  sourceFilenameDisplay: string;
  /**
   * Phase 5 — browser-side public-access probe results (§7.8). Always
   * present; an empty array means the configuration contained no
   * probe-eligible URLs. When the caller passes `probesEnabled: false`,
   * every target is reported as `probe-disabled`.
   */
  probeResults: ProbeTargetResult[];
}

/**
 * Main-thread pre-flight cap on the raw file size before the worker is
 * even spawned. Matches `MAX_XML_CONTENT_SIZE_BYTES` in
 * `src/parser/xml-security.ts` (100 MB); kept as a local constant so
 * this module does not pull `fast-xml-parser` into the main-thread
 * bundle.
 */
const MAX_PARSE_FILE_BYTES = 100 * 1024 * 1024;

/**
 * Number of leading bytes inspected by `assertLikelyXml` to confirm the
 * upload looks like an XML document before the worker is spawned. Large
 * enough to skip a UTF-8 BOM and any leading whitespace, small enough
 * that the slice is effectively free.
 */
const XML_SIGNATURE_PROBE_BYTES = 1024;

/**
 * Best-effort, main-thread guard that rejects obvious non-XML uploads
 * (e.g. ZIPs, PDFs, images, accidentally-selected binaries) before the
 * Web Worker is spawned and `FileReader.text()` decodes the entire
 * payload. The downstream parser/validator still performs the real
 * structural checks; this is a UX-grade short-circuit, not a security
 * boundary.
 */
async function assertLikelyXml(file: File): Promise<void> {
  const sliceBytes = Math.min(file.size, XML_SIGNATURE_PROBE_BYTES);
  if (sliceBytes === 0) {
    throw new Error('The selected file is empty.');
  }
  const head = await file.slice(0, sliceBytes).text();
  // Strip UTF-8 BOM if present and any leading whitespace.
  const trimmed = head.replace(/^\uFEFF/, '').trimStart();
  if (!trimmed.startsWith('<')) {
    throw new Error(
      'The selected file does not look like an XML document. ' +
        'Export the GeoEvent configuration as XML and try again.',
    );
  }
}

/**
 * Runs the parser+validator pipeline in a Web Worker. Resolves with the
 * parsed config; rejects with a descriptive Error if the worker reports
 * an error or fails to load.
 *
 * The returned promise also surfaces progress messages via `onProgress`.
 * The worker is terminated when the promise settles, regardless of
 * outcome, so callers do not have to manage worker lifecycle.
 */
export function runParsePipeline(
  file: File,
  onProgress: (event: OrchestratorProgressEvent) => void = () => {},
  options: OrchestratorOptions = {},
): Promise<ParsePipelineResult> {
  return new Promise<ParsePipelineResult>((resolve, reject) => {
    // Pre-flight guards — fail fast before spawning the worker.
    if (file.size > MAX_PARSE_FILE_BYTES) {
      const limitMb = Math.round(MAX_PARSE_FILE_BYTES / (1024 * 1024));
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      reject(
        new Error(
          `File is ${sizeMb} MB, which exceeds the ${limitMb} MB limit for GeoEvent configuration exports.`,
        ),
      );
      return;
    }

    assertLikelyXml(file).then(
      () => {
        startWorker();
      },
      (err: unknown) => {
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );

    const startWorker = (): void => {
    let worker: MinimalWorker;
    try {
      worker = options.workerFactory
        ? options.workerFactory()
        : (new Worker(options.workerUrl ?? 'assets/migration-worker.js', { type: 'module' }) as unknown as MinimalWorker);
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
      return;
    }

    let settled = false;
    let parsedPayload: ParsePipelineResult | null = null;
    const finish = (cb: () => void): void => {
      if (settled) return;
      settled = true;
      try {
        worker.terminate();
      } catch {
        // best-effort
      }
      cb();
    };

    worker.addEventListener('message', (ev) => {
      const msg = ev.data;
      switch (msg.type) {
        case 'progress':
          onProgress({ stage: msg.stage, message: msg.message });
          break;
        case 'parsed':
          parsedPayload = {
            config: msg.config,
            validation: msg.validation,
            sourceFilenameDisplay: msg.sourceFilenameDisplay,
            probeResults: [],
          };
          break;
        case 'probed':
          if (parsedPayload) {
            parsedPayload.probeResults = msg.results;
            finish(() => resolve(parsedPayload as ParsePipelineResult));
          } else {
            finish(() => reject(new Error('worker emitted probed before parsed')));
          }
          break;
        case 'error':
          finish(() => reject(new Error((msg as ErrorMessage).message)));
          break;
      }
    });

    worker.addEventListener('error', (ev) => {
      // ErrorEvent.message is empty in many browsers for cross-origin
      // worker scripts or for OOM/unrecoverable crashes; the worker's
      // own try/catch in `worker.ts` will normally post a structured
      // `{ type: 'error', message }` first. This handler is the
      // fallback for truly unrecoverable failures — keep the message
      // user-actionable even when the underlying detail is missing.
      const raw = ev.message?.trim();
      const detail = raw ? `: ${raw}` : '';
      finish(() =>
        reject(
          new Error(
            `The migration worker crashed unexpectedly${detail}. ` +
              `Try a smaller file or reload the page and try again.`,
          ),
        ),
      );
    });

    worker.postMessage({ type: 'parse', file, probesEnabled: options.probesEnabled });
    };
  });
}
